import { useState, useCallback, useMemo } from 'react';

export interface UsePaginationOptions {
  totalItems: number;
  pageSize?: number;
  initialPage?: number;
  pageSizeOptions?: number[];
}

export interface UsePaginationReturn {
  page: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  startIndex: number;
  endIndex: number;
  reset: () => void;
  pageSizeOptions?: number[];
}

export function usePagination({
  totalItems,
  pageSize: defaultPageSize = 20,
  initialPage = 0,
  pageSizeOptions,
}: UsePaginationOptions): UsePaginationReturn {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize]);

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(Math.max(0, Math.min(newPage, totalPages - 1)));
    },
    [totalPages],
  );

  const onPageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size);
      setPage(0);
    },
    [],
  );

  const startIndex = page * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const reset = useCallback(() => setPage(initialPage), [initialPage]);

  return { page, totalPages, pageSize, onPageChange, onPageSizeChange, startIndex, endIndex, reset, pageSizeOptions };
}
